import { useEffect } from "react";
import { SITE_NAME, SITE_OG_IMAGE, canonicalFor } from "@/lib/seo";

interface SeoProps {
  /** Título de la página (sin el sufijo de marca). */
  title: string;
  /** Descripción única de la página. */
  description?: string;
  /** Ruta pública, por ejemplo "/flota". Se usa para el canonical absoluto. */
  path: string;
  /** Directiva de indexación. Por defecto "index, follow". */
  robots?: string;
  /** Imagen para Open Graph / Twitter, si existe. */
  image?: string;
  /** Tipo Open Graph. Por defecto "website". */
  type?: string;
  /** Datos estructurados (JSON-LD) opcionales. */
  jsonLd?: Record<string, unknown>;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Componente de SEO por página.
 * - Establece el título y las etiquetas meta de forma dinámica.
 * - Actualiza el canonical absoluto (https://axoniagroup.com/...).
 * - Permite forzar noindex en páginas privadas (área de administración).
 */
export default function Seo({
  title,
  description,
  path,
  robots = "index, follow",
  image,
  type = "website",
  jsonLd,
}: SeoProps) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = canonicalFor(path);

    document.title = fullTitle;

    upsertMeta("name", "robots", robots);
    if (description) upsertMeta("name", "description", description);
    upsertCanonical(canonical);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:url", canonical);
    if (description) upsertMeta("property", "og:description", description);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    if (description) upsertMeta("name", "twitter:description", description);

    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:image", image);
    } else {
      upsertMeta("property", "og:image", SITE_OG_IMAGE);
      upsertMeta("name", "twitter:image", SITE_OG_IMAGE);
    }

    const existing = document.getElementById("seo-jsonld");
    if (existing) existing.remove();
    if (jsonLdString) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "seo-jsonld";
      script.textContent = jsonLdString;
      document.head.appendChild(script);
    }
  }, [title, description, path, robots, image, type, jsonLdString]);

  return null;
}